import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Auth } from 'aws-amplify';
import { addBreadcrumb } from '@sentry/react';

import appConfig, { EnvConfig } from '../config/env';
import {
    AuthHeader,
    AxiosAdapter,
    RequestConfig,
    RequestMethod,
} from './interfaces';
import { IAuthService } from '@/services/IAuthService';

const REQUESTS_WITH_PAYLOAD: RequestMethod[] = ['post', 'put', 'patch'];
const REQUESTS_WITHOUT_PAYLOAD: RequestMethod[] = ['delete', 'get'];

const getApiUrl = (config: EnvConfig): string => (config.API_PROXY ? config.API_PROXY.API_URL : config.API_URL);

class AxiosAmplifyWrapper implements AxiosAdapter {
    /*
    * Decorator for axios, which provides API like AWS Amplify. Required for authorize /access API requests
    */
    private readonly authService: IAuthService;

    private readonly httpInstance: AxiosInstance;

    constructor(authService: IAuthService) {
        this.authService = authService;
        this.httpInstance = axios.create({
            baseURL: getApiUrl(appConfig),
            withCredentials: false,
            headers: {
                'Content-Type': 'application/json',
            },
        });
    }

    private async tryGetAuthHeaders(): Promise<Partial<AuthHeader>> {
        // 1. Prefer the IdP user (WSO2 OIDC / Kerberos / domain-idp-service).
        //    Internal users authenticated via the IdP carry their access token
        //    and `profile.sub` as the canonical user id for backend /ext routes.
        try {
            const user = await this.authService.getUser();
            if (user) {
                return {
                    Authorization: `Bearer ${user.access_token}`,
                    SpecterxUserId: user.profile.sub,
                };
            }
        } catch (error) {
            // Don't block the request — fall through to the Cognito fallback.
            // Record a breadcrumb so the IdP failure shows up in any Sentry
            // event captured later in this request's lifecycle (helps prod
            // debugging when /ext requests come through unauthenticated).
            addBreadcrumb({
                category: 'auth',
                level: 'warning',
                message: 'IdP getUser failed; falling back to Cognito for /ext auth headers',
                data: { error: error instanceof Error ? error.message : String(error) },
            });
        }

        // 2. Fall back to Cognito for external recipients who authenticated
        //    via OTP on routes like /shared-file. They have no IdP session,
        //    but their Cognito id token + sub still identify them to the
        //    backend's /ext routes via the proxy.
        try {
            const cognitoUser = await Auth.currentAuthenticatedUser();
            const session = cognitoUser?.getSignInUserSession?.();
            const idToken = session?.getIdToken?.();
            const sub = idToken?.payload?.sub;
            const jwt = idToken?.getJwtToken?.();
            if (sub && jwt) {
                return {
                    Authorization: `Bearer ${jwt}`,
                    SpecterxUserId: sub,
                };
            }
        } catch {
            // No auth available - send the request without auth headers and
            // let the backend reject it; behaviour matches pre-fix (empty {}).
        }

        return {};
    }

    private async tryGetHeaders(params: RequestConfig): Promise<Record<string, string>> {
        const authHeaders: Partial<AuthHeader> = await this.tryGetAuthHeaders();
        const customHeaders = params.headers || {};
        return { ...authHeaders, ...customHeaders };
    }

    private async getRequestParams(init: RequestConfig): Promise<RequestConfig> {
        const headers = await this.tryGetHeaders(init);
        return { ...init, headers };
    }

    private async sendRequest(
        endpoint: string,
        init: RequestConfig,
        method: RequestMethod,
    ): Promise<any> {
        const { headers, body, ...other } = await this.getRequestParams(init);
        const options = { headers, ...other };
        let callback;
        if (REQUESTS_WITH_PAYLOAD.includes(method)) {
            callback = async () => this.httpInstance[method](endpoint, body, options);
        } else if (REQUESTS_WITHOUT_PAYLOAD.includes(method)) {
            callback = async () => this.httpInstance[method](endpoint, options);
        }
        const axiosResponse: AxiosResponse = await callback();
        return init.response ? axiosResponse : axiosResponse.data;
    }

    async get(apiName: string, endpoint: string, init: RequestConfig): Promise<any> {
        return this.sendRequest(endpoint, init, 'get');
    }

    async del(apiName: string, endpoint: string, init: RequestConfig): Promise<any> {
        return this.sendRequest(endpoint, init, 'delete');
    }

    async patch(apiName: string, endpoint: string, init: RequestConfig): Promise<any> {
        return this.sendRequest(endpoint, init, 'patch');
    }

    async put(apiName: string, endpoint: string, init: RequestConfig): Promise<any> {
        return this.sendRequest(endpoint, init, 'put');
    }

    async post(apiName: string, endpoint: string, init: RequestConfig): Promise<any> {
        return this.sendRequest(endpoint, init, 'post');
    }
}

export default AxiosAmplifyWrapper;
